import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TimelineIcon from '@mui/icons-material/Timeline';
import FlagIcon from '@mui/icons-material/Flag';
import InsightsIcon from '@mui/icons-material/Insights';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddTaskIcon from '@mui/icons-material/AddTask';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import { fetchRequest as fetchAvailableFields } from '../../redux/actions/segments/fields';
import {
  previewSegmentByConfigRequest,
  clearPreviewState,
} from '../../redux/actions/segments/preview';
import {
  createSegmentRequest,
  clearState as clearCreateState,
} from '../../redux/actions/segments/create';

import SignalPicker from './studio/SignalPicker';
import RangePicker from './studio/RangePicker';
import NormalizationControl from './studio/NormalizationControl';
import EngineeredFeatures from './studio/EngineeredFeatures';
import PreviewChart from './studio/PreviewChart';
import LiveDataGrid from './studio/LiveDataGrid';
import {
  deriveAutoConfig,
  buildSegmentConfig,
  sanitizePreview,
  getPreset,
  getFieldRows,
  colorForSignal,
  cadenceIntervalForKeys,
  buildSignalTracks,
  recommendedEngineeredConfigs,
} from './studio/studioUtils';

const PREVIEW_PAGE_SIZE = 200;

const SegmentStudio = ({
  fieldsData,
  fieldsLoading,
  fetchAvailableFields,
  previewData,
  previewLoading,
  previewError,
  previewSegmentByConfig,
  clearPreview,
  createLoading,
  createResponse,
  createError,
  createSegment,
  clearCreate,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const workspaceId =
    typeof window !== 'undefined' ? localStorage.getItem('WORKSPACE_ID') : null;

  // ---- Studio state ------------------------------------------------------
  const [name, setName] = useState('');
  const [features, setFeatures] = useState([]);
  const [labels, setLabels] = useState([]);
  const [interval, setIntervalSec] = useState(3600);
  const [tolerance, setTolerance] = useState(5400);
  const [tailSeconds, setTailSeconds] = useState(31536000);
  const [live, setLive] = useState(false);
  // Explicit end of a historical window (null = anchor to the latest data).
  const [endMs, setEndMs] = useState(null);
  const [preset, setPreset] = useState('smart');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [globalStrategy, setGlobalStrategy] = useState(getPreset('smart').strategy);
  const [perField, setPerField] = useState({});
  const [engineeredFeatures, setEngineeredFeatures] = useState([]);

  // User-touch guards so auto-config never clobbers a deliberate choice.
  const seededRef = useRef(false);
  const touchedNameRef = useRef(false);
  const touchedStrategyRef = useRef(false);
  const touchedIntervalRef = useRef(false);

  // ---- Bootstrap ---------------------------------------------------------
  useEffect(() => {
    if (!fieldsData && !fieldsLoading) fetchAvailableFields();
    clearCreate();
    return () => {
      clearPreview();
      clearCreate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Seed smart defaults once fields arrive ----------------------------
  const auto = useMemo(() => deriveAutoConfig(fieldsData), [fieldsData]);
  useEffect(() => {
    if (seededRef.current || !fieldsData) return;
    seededRef.current = true;
    setFeatures(auto.features);
    setLabels(auto.labels);
    setIntervalSec(auto.interval);
    setTolerance(auto.tolerance);
    setTailSeconds(auto.tailSeconds);
    setLive(Boolean(auto.live));
    const recEng = recommendedEngineeredConfigs(fieldsData, getFieldRows(fieldsData));
    if (recEng.length > 0) setEngineeredFeatures(recEng);
    if (!touchedNameRef.current) {
      const tgt = auto.labels[0];
      setName(tgt ? `Predict ${tgt}${auto.labels.length > 1 ? ` +${auto.labels.length - 1}` : ''}` : 'New segment');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsData]);

  // Keep the timeline resolution honest: track the real cadence of whatever
  // signals are selected (slowest wins, bucket-count clamped) until the user
  // dials in their own interval. This is what stops a sparse, hourly signal
  // from being sampled at 1-second granularity (millions of empty rows).
  useEffect(() => {
    if (touchedIntervalRef.current || !fieldsData) return;
    const selected = [...features, ...labels];
    if (selected.length === 0) return;
    const ci = cadenceIntervalForKeys(fieldsData, selected, tailSeconds);
    if (ci && ci !== interval) {
      setIntervalSec(ci);
      setTolerance(Math.max(1, Math.round(ci * 1.5)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features, labels, tailSeconds, fieldsData]);

  // When the preset changes (and the user hasn't hand-edited the global
  // strategy in advanced mode), mirror the preset recipe into globalStrategy.
  useEffect(() => {
    if (!touchedStrategyRef.current) {
      setGlobalStrategy(getPreset(preset).strategy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  // ---- Derived data ------------------------------------------------------
  const fieldRows = useMemo(() => getFieldRows(fieldsData), [fieldsData]);
  const fieldRowsByKey = useMemo(() => {
    const m = {};
    fieldRows.forEach((r) => {
      m[r.key] = r;
    });
    return m;
  }, [fieldRows]);

  const labelRecs = useMemo(
    () =>
      (fieldsData?.latest_analysis?.results?.label_recommendations || [])
        .map((r) => (typeof r === 'string' ? r : r.name))
        .filter(Boolean),
    [fieldsData],
  );
  const featureRecs = useMemo(
    () =>
      (fieldsData?.latest_analysis?.results?.feature_recommendations?.available || [])
        .map((r) => (typeof r === 'string' ? r : r.name))
        .filter(Boolean),
    [fieldsData],
  );

  const strategyOptions = useMemo(() => {
    const prim = fieldsData?.normalization_strategies?.primary_strategies;
    if (Array.isArray(prim) && prim.length) {
      return prim.map((s) => ({ id: s.id, name: s.name }));
    }
    return null;
  }, [fieldsData]);

  // Stable colour per selected signal (features first, then targets).
  const colorMap = useMemo(() => {
    const order = [];
    features.forEach((k) => order.includes(k) || order.push(k));
    labels.forEach((k) => order.includes(k) || order.push(k));
    const m = {};
    order.forEach((k, i) => {
      m[k] = colorForSignal(k, i);
    });
    return m;
  }, [features, labels]);

  const colorFnFeatures = useMemo(
    () => (key) => colorMap[key] || colorForSignal(key),
    [colorMap],
  );

  // Per-signal density tracks for the range timeline. Union of targets +
  // features, de-duplicated (a signal can legitimately be both).
  const tracks = useMemo(() => {
    const order = [];
    features.forEach((k) => order.includes(k) || order.push(k));
    labels.forEach((k) => order.includes(k) || order.push(k));
    return buildSignalTracks(fieldsData, order, colorMap, fieldRowsByKey);
  }, [features, labels, fieldsData, colorMap, fieldRowsByKey]);

  const sanitized = useMemo(
    () => sanitizePreview(previewData, fieldRowsByKey),
    [previewData, fieldRowsByKey],
  );

  // ---- Live preview (debounced) -----------------------------------------
  // Resolve the explicit historical window (null end = anchor to latest).
  const computeWindow = () => {
    const end = live
      ? Date.now()
      : endMs != null
      ? endMs
      : auto.workspaceEndMs;
    const start = end != null ? end - tailSeconds * 1000 : null;
    return { windowStartMs: start, windowEndMs: end };
  };

  const previewKey = useMemo(
    () =>
      JSON.stringify({
        features: [...features].sort(),
        labels: [...labels].sort(),
        interval,
        tolerance,
        tailSeconds,
        live,
        endMs,
        preset,
        globalStrategy,
        perField,
        engineeredFeatures,
      }),
    [features, labels, interval, tolerance, tailSeconds, live, endMs, preset, globalStrategy, perField, engineeredFeatures],
  );

  useEffect(() => {
    if (!workspaceId || features.length === 0) return undefined;
    const { windowStartMs, windowEndMs } = computeWindow();
    const cfg = buildSegmentConfig({
      name,
      features,
      labels,
      interval,
      tolerance,
      tailSeconds,
      live,
      windowStartMs,
      windowEndMs,
      preset,
      advanced: { globalStrategy, perField },
      engineeredFeatures,
      workspaceId,
    });
    const t = setTimeout(() => {
      previewSegmentByConfig({ config: cfg, page: 1, page_size: PREVIEW_PAGE_SIZE });
    }, 550);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, workspaceId]);

  // ---- Navigate on successful create ------------------------------------
  useEffect(() => {
    const uid =
      createResponse?.data?.uid ||
      createResponse?.uid ||
      createResponse?.data?.segment?.uid ||
      createResponse?.segment_id;
    if (uid) {
      enqueueSnackbar('Segment created', { variant: 'success' });
      navigate(`/segments/${uid}`);
    }
  }, [createResponse, navigate, enqueueSnackbar]);

  // ---- Handlers ----------------------------------------------------------
  const canCreate = features.length > 0 && labels.length > 0 && !!name.trim();

  const handleCreate = () => {
    if (!canCreate) {
      enqueueSnackbar('Add at least one target and one feature, and a name.', {
        variant: 'warning',
      });
      return;
    }
    const { windowStartMs, windowEndMs } = computeWindow();
    const cfg = buildSegmentConfig({
      name,
      features,
      labels,
      interval,
      tolerance,
      tailSeconds,
      live,
      windowStartMs,
      windowEndMs,
      preset,
      advanced: { globalStrategy, perField },
      engineeredFeatures,
      workspaceId,
    });
    createSegment(cfg);
  };

  // ---- Render ------------------------------------------------------------
  const showInitialLoader = fieldsLoading && !fieldsData;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
      >
        <PageBreadcrumbs
          items={[
            { text: 'Segments', icon: <TimelineIcon />, link: '/segments' },
            { text: 'New segment' },
          ]}
          mb={0}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            startIcon={createLoading ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon />}
            disabled={!canCreate || createLoading}
            onClick={handleCreate}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {createLoading ? 'Creating…' : 'Create segment'}
          </Button>
        </Stack>
      </Stack>

      {createError && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => clearCreate()}>
          {createError}
        </Alert>
      )}

      {showInitialLoader ? (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Reading your workspace…
          </Typography>
        </Box>
      ) : (
        <Box sx={{ mt: 1.5 }}>
          {/* ---------------- Row 1: Signals + Fill gaps ---------------- */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {/* Name + Signals */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Stack spacing={2.5}>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                    <InsightsIcon fontSize="small" sx={{ color: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Segment name
                    </Typography>
                  </Stack>
                  <TextField
                    size="small"
                    fullWidth
                    value={name}
                    placeholder="Name this segment"
                    onChange={(e) => {
                      touchedNameRef.current = true;
                      setName(e.target.value);
                    }}
                  />
                </Box>

                <SignalPicker
                  label="Predict (targets)"
                  placeholder="What should the model forecast?"
                  helper="The signal(s) you want to predict. We highlight the ones your analysis flagged as good targets."
                  icon={<FlagIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />}
                  fieldRows={fieldRows}
                  value={labels}
                  onChange={setLabels}
                  recommended={labelRecs}
                  accent={theme.palette.warning.main}
                  colorFn={() => theme.palette.warning.main}
                />

                <SignalPicker
                  label="From signals (features)"
                  placeholder="Which inputs drive the prediction?"
                  helper="The inputs the model learns from. Auto-picked from the strongest correlations — tweak freely."
                  icon={<AutoAwesomeIcon fontSize="small" sx={{ color: 'primary.main' }} />}
                  fieldRows={fieldRows}
                  value={features}
                  onChange={setFeatures}
                  recommended={featureRecs}
                  colorFn={colorFnFeatures}
                />

                <Divider />

                <EngineeredFeatures
                  fieldRows={fieldRows}
                  fieldsData={fieldsData}
                  value={engineeredFeatures}
                  onChange={setEngineeredFeatures}
                />
              </Stack>
            </Paper>

            {/* Normalization */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <NormalizationControl
                presetId={preset}
                onSelectPreset={(id) => {
                  touchedStrategyRef.current = false;
                  setPreset(id);
                }}
                advancedOpen={advancedOpen}
                onToggleAdvanced={() => setAdvancedOpen((o) => !o)}
                interval={interval}
                tolerance={tolerance}
                onChangeInterval={(v) => {
                  touchedIntervalRef.current = true;
                  setIntervalSec(v);
                }}
                onChangeTolerance={(v) => {
                  touchedIntervalRef.current = true;
                  setTolerance(v);
                }}
                globalStrategy={globalStrategy}
                onChangeGlobalStrategy={(s) => {
                  touchedStrategyRef.current = true;
                  setGlobalStrategy(s);
                }}
                perField={perField}
                onChangePerField={setPerField}
                fieldRows={fieldRows}
                strategyOptions={strategyOptions}
              />
            </Paper>
          </Box>

          {/* ---------------- Row 2: Time window (full width) ---------------- */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
            <RangePicker
              workspaceStartMs={auto.workspaceStartMs}
              workspaceEndMs={auto.workspaceEndMs}
              tailSeconds={tailSeconds}
              onChangeTail={setTailSeconds}
              live={live}
              onToggleLive={(v) => {
                setLive(v);
                if (v) setEndMs(null);
              }}
              interval={interval}
              tracks={tracks}
              endMs={endMs}
              onChangeEnd={setEndMs}
            />
          </Paper>

          {/* ---------------- Preview surface (full width) ---------------- */}
          <Paper
            variant="outlined"
            sx={{ borderRadius: 3, overflow: 'hidden', mt: 2 }}
          >
            <Box sx={{ position: 'relative' }}>
              {previewLoading && (
                <LinearProgress
                  sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5, height: 2 }}
                />
              )}

              {/* Empty / guidance state */}
              {features.length === 0 ? (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                  <AutoAwesomeIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    Pick a signal to begin
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose what to predict and the features that drive it — your
                    data renders here instantly.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ height: 360, p: 1.5, pb: 0 }}>
                    <PreviewChart sanitized={sanitized} colorMap={colorMap} />
                  </Box>

                  {previewError && (
                    <Alert severity="error" sx={{ mx: 1.5, my: 1 }}>
                      {previewError}
                    </Alert>
                  )}

                  <Divider />

                  <Box sx={{ p: 1.5 }}>
                    {sanitized ? (
                      <LiveDataGrid sanitized={sanitized} colorMap={colorMap} maxHeight={520} />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'text.secondary',
                        }}
                      >
                        {previewLoading ? (
                          <Stack alignItems="center" spacing={1}>
                            <CircularProgress size={22} />
                            <Typography variant="body2">Building your grid…</Typography>
                          </Stack>
                        ) : (
                          <Typography variant="body2">Adjust the controls to preview data.</Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  fieldsData: state.segment_fields?.response,
  fieldsLoading: state.segment_fields?.isLoading || false,
  previewData: state.segment_preview?.response || null,
  previewLoading: state.segment_preview?.isLoading || false,
  previewError: state.segment_preview?.error || null,
  createLoading: state.segment_create?.isLoading || false,
  createResponse: state.segment_create?.response || null,
  createError: state.segment_create?.error || null,
});

const mapDispatchToProps = {
  fetchAvailableFields,
  previewSegmentByConfig: previewSegmentByConfigRequest,
  clearPreview: clearPreviewState,
  createSegment: createSegmentRequest,
  clearCreate: clearCreateState,
};

export default connect(mapStateToProps, mapDispatchToProps)(SegmentStudio);
